import axios from 'axios';
import { axiosETAGCache } from 'axios-etag-cache';

let globalImportedDocumentSchema = null;
let globalImportedWebpageSchema = null;
let globalKnowledgeChunkSchema = null;
let globalAnalysisSchema = null;

// Apply the axios ETAG interceptor
const axiosWithETAGCache = axiosETAGCache(axios);
const api = {
  async getImportedDocumentSchema() {
    if (globalImportedDocumentSchema) {
      return globalImportedDocumentSchema;
    }
    const response = await axiosWithETAGCache.get(`/imported_document/schema`);
    globalImportedDocumentSchema = response.data;
    return response.data;
  },
  async getImportedDocuments(tableQuery) {
    const queryParams = {
      query: JSON.stringify(tableQuery),
    };

    const response = await axiosWithETAGCache.get(`/imported_document`, {
      params: queryParams,
    });

    return response.data;
  },
  async createNewImportedDocument(newDocumentData) {
    const response = await axios.post(`/imported_document`, newDocumentData);

    return response.data;
  },
  async getImportedDocument(documentId) {
    const queryParams = {};

    const response = await axiosWithETAGCache.get(
      `/imported_document/${documentId}`,
      {
        params: queryParams,
      }
    );

    return response.data;
  },
  async saveImportedDocument(document) {
    const response = await axios.put(
      `/imported_document/${document.id}`,
      document
    );
    return response.data;
  },
  async deleteImportedDocument(documentId, deleteKnowledgeChunks = true) {
    const response = await axios.delete(`/imported_document/${documentId}`, {
      params: { delete_knowledge_chunks: deleteKnowledgeChunks },
    });

    return response.data;
  },
  async getKnowledgeChunkSchema() {
    if (globalKnowledgeChunkSchema) {
      return globalKnowledgeChunkSchema;
    }
    const response = await axiosWithETAGCache.get(`/knowledge_chunk/schema`);
    globalKnowledgeChunkSchema = response.data;
    return response.data;
  },
  async getKnowledgeChunks(tableQuery) {
    const queryParams = {
      query: JSON.stringify(tableQuery),
    };

    const response = await axiosWithETAGCache.get(`/knowledge_chunk`, {
      params: queryParams,
    });

    return response.data;
  },
  async createNewKnowledgeChunk(newKnowledgeChunkData) {
    const response = await axios.post(
      `/knowledge_chunk`,
      newKnowledgeChunkData
    );

    return response.data;
  },
  async getKnowledgeChunk(knowledgeChunkId) {
    const queryParams = {};

    const response = await axiosWithETAGCache.get(
      `/knowledge_chunk/${knowledgeChunkId}`,
      {
        params: queryParams,
      }
    );

    return response.data;
  },
  async saveKnowledgeChunk(knowledge_chunk) {
    const response = await axios.put(
      `/knowledge_chunk/${knowledge_chunk.id}`,
      knowledge_chunk
    );
    return response.data;
  },
  async deleteKnowledgeChunk(knowledgeChunkId) {
    const response = await axios.delete(`/knowledge_chunk/${knowledgeChunkId}`);

    return response.data;
  },
  /**
   * Get all knowledge chunks associated with a specific source (imported webpage or document).
   * @param {string} sourceId - The originating_object_id (e.g., imwe_xxx or imdo_xxx)
   * @returns {Promise<Array>} Array of knowledge chunks
   */
  async getKnowledgeChunksBySourceId(sourceId) {
    const tableQuery = {
      startRow: 0,
      endRow: 500, // Reasonable limit for display
      filterModel: {
        originating_object_id: {
          filterType: 'text',
          type: 'equals',
          filter: sourceId,
        },
      },
      sortModel: [{ colId: 'created_at', sort: 'desc' }],
      fields: ['matching_text', 'content_text', 'created_at', 'source_type'],
    };

    const queryParams = {
      query: JSON.stringify(tableQuery),
    };

    const response = await axiosWithETAGCache.get(`/knowledge_chunk`, {
      params: queryParams,
    });

    // API returns array directly, not wrapped in object
    return response.data || [];
  },
  /**
   * Delete multiple knowledge chunks by their IDs.
   * @param {string[]} chunkIds - Array of knowledge chunk IDs to delete
   * @returns {Promise<{success: string[], failed: string[]}>} Results of deletion
   */
  async deleteKnowledgeChunks(chunkIds) {
    const results = { success: [], failed: [] };

    for (const chunkId of chunkIds) {
      try {
        await axios.delete(`/knowledge_chunk/${chunkId}`);
        results.success.push(chunkId);
      } catch (error) {
        console.error(`Failed to delete chunk ${chunkId}:`, error);
        results.failed.push(chunkId);
      }
    }

    return results;
  },
  async getKnowledgeBaseConfiguration() {
    const response = await axios.get(`/knowledge_base_configuration`);
    return response.data;
  },
  async saveKnowledgeBaseConfiguration(data) {
    const response = await axios.put(`/knowledge_base_configuration`, data);
    return response.data;
  },
  async createNewImportedDocumentWithFileUpload(file, onUploadProgress) {
    const formData = new FormData();
    formData.append(file.name, file);

    let handle = null;

    const options = {
      onUploadProgress: function (progressEvent) {
        const proportionCompleted = progressEvent.loaded / progressEvent.total;
        if (onUploadProgress) {
          onUploadProgress(proportionCompleted * 0.2);
        }

        if (progressEvent.loaded === progressEvent.total) {
          if (onUploadProgress) {
            onUploadProgress(0.2);
          }

          let progress = 0.2;
          // Now start an interval to update the progress bar until the file finishes getting processed.
          handle = setInterval(async () => {
            progress += (1.0 - progress) * 0.01;
            if (onUploadProgress) {
              onUploadProgress(progress);
            }
          }, 250);
        }
      },
      onDownloadProgress: function (progressEvent) {
        if (progressEvent.loaded === progressEvent.total) {
          if (handle) {
            clearInterval(handle);
          }
          if (onUploadProgress) {
            onUploadProgress(1.0);
          }
        }
      },
    };

    const response = await axios.post(`/imported_document`, formData, options);
    if (onUploadProgress) {
      onUploadProgress(1.0);
    }
    return response.data;
  },

  async queryKnowledgeBase(queryText) {
    const queryParams = {
      query: queryText,
    };

    const response = await axiosWithETAGCache.get(`/knowledge/search`, {
      params: queryParams,
    });

    return response.data;
  },

  async getImportedWebpageSchema() {
    if (globalImportedWebpageSchema) {
      return globalImportedWebpageSchema;
    }
    const response = await axiosWithETAGCache.get(`/imported_webpage/schema`);
    globalImportedWebpageSchema = response.data;
    return response.data;
  },

  async getImportedWebpages(tableQuery) {
    const queryParams = {
      query: JSON.stringify(tableQuery),
    };

    const response = await axiosWithETAGCache.get(`/imported_webpage`, {
      params: queryParams,
    });

    return response.data;
  },

  async importWebpage(url, method) {
    const data = {
      original_url: url,
      method: method,
    };

    const response = await axios.post(`/imported_webpage`, data);
    return response.data;
  },

  async getImportedWebpage(importedWebpageId) {
    const queryParams = {};

    const response = await axiosWithETAGCache.get(
      `/imported_webpage/${importedWebpageId}`,
      {
        params: queryParams,
      }
    );

    return response.data;
  },

  async saveImportedWebpage(importedWebpage) {
    const response = await axios.put(
      `/imported_webpage/${importedWebpage.id}`,
      importedWebpage
    );
    return response.data;
  },

  async deleteImportedWebpage(importedWebpageId, deleteKnowledgeChunks = true) {
    const response = await axios.delete(
      `/imported_webpage/${importedWebpageId}`,
      {
        params: { delete_knowledge_chunks: deleteKnowledgeChunks },
      }
    );

    return response.data;
  },
  async getAnalysisSchema() {
    if (globalAnalysisSchema) {
      return globalAnalysisSchema;
    }
    const response = await axiosWithETAGCache.get(`/analysis/schema`);
    globalAnalysisSchema = response.data;
    return response.data;
  },
  async getAnalysisList(tableQuery) {
    const queryParams = {
      query: JSON.stringify(tableQuery),
    };

    const response = await axiosWithETAGCache.get(`/analysis`, {
      params: queryParams,
    });

    return response.data;
  },
  async performAnalysis(goal_prompt, iterations) {
    const data = {
      goal_prompt: goal_prompt,
      iterations: iterations,
    };

    const response = await axios.post(`/analysis`, data);
    return response.data;
  },

  async getAnalysis(AnalysisId) {
    const queryParams = {};

    const response = await axiosWithETAGCache.get(`/analysis/${AnalysisId}`, {
      params: queryParams,
    });

    return response.data;
  },

  async saveAnalysis(Analysis) {
    const response = await axios.put(`/analysis/${Analysis.id}`, Analysis);
    return response.data;
  },

  async deleteAnalysis(AnalysisId) {
    const response = await axios.delete(`/analysis/${AnalysisId}`);

    return response.data;
  },
  async searchImportedDocuments(queryText) {
    const queryParams = {
      query: queryText,
    };

    const response = await axiosWithETAGCache.get(`/imported_document/search`, {
      params: queryParams,
    });

    return response.data;
  },
  async searchImportedWebpages(queryText) {
    const queryParams = {
      query: queryText,
    };

    const response = await axiosWithETAGCache.get(`/imported_webpage/search`, {
      params: queryParams,
    });

    return response.data;
  },
};

export default api;
